import React from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { TRUST_PILLARS } from "@/data/content";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";

export default function Trust() {
    const { openDemo } = useOutletContext();
    return (
        <div className="bg-warm">
            <section className="relative overflow-hidden pt-20 pb-16 bg-warm">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="aurora-blob blob-violet drift-a" style={{ top: "-10%", left: "10%", width: 420, height: 420 }} />
                    <div className="aurora-blob blob-pink drift-b" style={{ top: "40%", right: "10%", width: 340, height: 340 }} />
                </div>
                <div className="container-x relative z-10 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <Reveal>
                            <div className="text-sm text-slate-500 mb-4">Trust &amp; governance</div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1 className="heading-hero text-5xl sm:text-6xl lg:text-7xl">
                                AI you can put in front of
                                <br />
                                <span className="muted">regulators, auditors, and customers.</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-8 text-slate-600 max-w-2xl text-base md:text-lg leading-relaxed">
                                Augmentation only works if it&apos;s trustworthy. Every NyraAI deployment is built on
                                a foundation of security, governance, and transparency.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <div className="mt-8">
                                <button onClick={openDemo} className="pill-dark" data-testid="trust-book-demo">
                                    Review security with us
                                </button>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.3} className="lg:col-span-4">
                        <div className="mesh-frame p-8">
                            <div className="glass-card p-6">
                                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-4">
                                    Audit-ready
                                </div>
                                <ul className="space-y-3 text-sm">
                                    {[
                                        "Sources on every answer",
                                        "Confidence scoring",
                                        "Human approvals logged",
                                        "Full step-by-step trail",
                                        "Role-based access enforced",
                                    ].map((t) => (
                                        <li key={t} className="flex items-center gap-3">
                                            <div className="w-6 h-6 rounded-full grid place-items-center bg-slate-900 text-white shrink-0">
                                                <i className="fa-solid fa-check text-[10px]" />
                                            </div>
                                            <div className="text-slate-800">{t}</div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="pb-20 bg-warm">
                <div className="container-x">
                    <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {TRUST_PILLARS.map((p) => (
                            <StaggerChild key={p.title}>
                                <motion.div
                                    whileHover={{ y: -3 }}
                                    transition={{ duration: 0.3 }}
                                    className="gleap-card p-8 h-full"
                                    data-testid={`trust-pillar-${p.title
                                        .split(",")[0]
                                        .toLowerCase()
                                        .replace(/[^a-z]+/g, "-")}`}
                                >
                                    <div className="w-11 h-11 rounded-xl grid place-items-center bg-slate-900 text-white">
                                        <i className={p.icon} />
                                    </div>
                                    <h3
                                        className="font-serif text-2xl text-slate-900 mt-6"
                                    >
                                        {p.title}
                                    </h3>
                                    <p className="text-slate-600 mt-3 leading-relaxed text-sm">{p.body}</p>
                                </motion.div>
                            </StaggerChild>
                        ))}
                    </StaggerGroup>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-warm">
                <div className="container-x text-center max-w-3xl mx-auto">
                    <Reveal>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl">
                            Review our security architecture
                            <br />
                            <span className="muted">with your team.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-10">
                            <button onClick={openDemo} className="pill-dark">
                                Book a demo
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
